/**
 * Security utilities for safe redirect handling
 * Prevents open redirect vulnerabilities
 */

const ALLOWED_REDIRECTS = [
  '/dashboard',
  '/onboarding',
  '/settings',
  '/quiz',
  '/start', // email deep-link handler — must survive the sign-in round-trip
  '/exam-results',
  '/progress',
  '/flashcards',
  '/sims',
  '/ask-pam',
  '/feedback',
  '/pricing',
  '/checkout',
  '/profile',
  '/subscription',
] as const;

const DEFAULT_REDIRECT = '/dashboard';

/**
 * Returns the normalized same-origin path for raw, else fallback.
 * Browsers read `\` as `/`, so `/\evil.example` would leave the site.
 */
export function safeReturnPath(raw: unknown, fallback = DEFAULT_REDIRECT): string {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return fallback;
  }
  try {
    const { origin } = window.location;
    const url = new URL(raw, origin);
    // Dot segments can collapse to `//host` after parsing (`/..//evil`).
    if (url.origin !== origin || url.pathname.startsWith('//')) return fallback;
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}

/**
 * Sanitizes and validates a redirect URL to prevent open redirect attacks
 *
 * Security measures:
 * - Only allows relative URLs starting with /
 * - Blocks protocol-relative URLs (//)
 * - Removes path traversal attempts (../)
 * - Validates against allowlist of safe paths
 * - Prevents subdomain attacks (e.g., /dashboard.evil.com)
 *
 * @param url - The URL to validate (typically from query params)
 * @returns Safe redirect URL or default dashboard URL
 */
export function sanitizeRedirectUrl(url: string | null): string {
  // If no URL provided, use default
  if (!url) {
    return DEFAULT_REDIRECT;
  }

  try {
    // Decode the URL
    const decoded = decodeURIComponent(url);

    // Security: Only allow relative URLs starting with exactly one /
    const safe = safeReturnPath(decoded, '');
    if (!safe) {
      console.warn('[Security] Blocked non-relative redirect:', decoded);
      return DEFAULT_REDIRECT;
    }

    // Security: Remove any path traversal attempts
    const cleanUrl = decoded.replace(/\.\./g, '');
    if (cleanUrl !== decoded) {
      console.warn('[Security] Blocked path traversal in redirect:', decoded);
      return DEFAULT_REDIRECT;
    }

    // Get the base path (remove query params and hash)
    const basePath = safe.split('?')[0].split('#')[0];

    // Security: Validate against allowlist
    // Check if the path matches or is a subpath of an allowed redirect
    const isValid = ALLOWED_REDIRECTS.some(allowedPath => {
      // Exact match
      if (basePath === allowedPath) return true;

      // Subpath match (e.g., /quiz/session-123 matches /quiz)
      // But /quizmalicious should NOT match /quiz
      if (basePath.startsWith(`${allowedPath}/`)) return true;

      return false;
    });

    if (!isValid) {
      console.warn('[Security] Blocked redirect to disallowed path:', basePath);
      return DEFAULT_REDIRECT;
    }

    return safe;

  } catch (error) {
    console.error('[Security] Error sanitizing redirect URL:', error);
    return DEFAULT_REDIRECT;
  }
}

/**
 * Check if a redirect URL is valid without sanitizing
 * Useful for validation without modification
 */
export function isValidRedirectUrl(url: string | null): boolean {
  if (!url) return false;

  try {
    const sanitized = sanitizeRedirectUrl(url);
    return sanitized === decodeURIComponent(url).replace(/\.\./g, '');
  } catch {
    return false;
  }
}

/**
 * Get the list of allowed redirect paths (for testing/debugging)
 */
export function getAllowedRedirects(): readonly string[] {
  return ALLOWED_REDIRECTS;
}
